import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ScanFace, ArrowRight, Instagram } from "lucide-react";
import { Reveal, MaskReveal } from "../components/Reveal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { SERVICES, PARAMETERS } from "../lib/data";
import { getProducts } from "../lib/api";
import { useCart } from "../context/CartContext";

const HERO_BG =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_glow-lab-5/artifacts/lefim0at_download.jpeg";
const BEFORE_IMG =
  "https://images.unsplash.com/photo-1668596808908-ac4d45963462?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHw0fHx3b21hbiUyMGNsZWFyJTIwc2tpbiUyMGx1eHVyeSUyMGJlYXV0eXxlbnwwfHx8fDE3ODU2OTc5Mzh8MA&ixlib=rb-4.1.0&q=85";
const AFTER_IMG =
  "https://images.unsplash.com/photo-1741936362056-d9931fbdc148?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHx3b21hbiUyMGNsZWFyJTIwc2tpbiUyMGx1eHVyeSUyMGJlYXV0eXxlbnwwfHx8fDE3ODU2OTc5Mzh8MA&ixlib=rb-4.1.0&q=85";

function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 140]);
  const scale = useTransform(scrollY, [0, 600], [1, 1.12]);
  return (
    <section className="relative min-h-screen flex items-end overflow-hidden pb-20 pt-32">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={HERO_BG} alt="Women Heaven medical spa reception" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/15 to-black/10" />
      </motion.div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
        <h1 className="font-display font-semibold tracking-tighter text-[#EBD9A2] leading-[0.92] text-6xl sm:text-7xl lg:text-[8.5rem]" style={{ textShadow: "0 2px 30px rgba(0,0,0,0.35)" }}>
          <MaskReveal delay={0.15}>The art of</MaskReveal>
          <MaskReveal delay={0.3}><span className="italic">luminous</span> skin</MaskReveal>
        </h1>
        <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <Reveal delay={0.6} className="max-w-md">
            <p className="text-white/90 text-lg leading-relaxed" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.4)" }}>
              Advanced CO2 laser, IPL photofacial and restorative cupping — guided by
              AI skin analysis across 12 clinical parameters.
            </p>
          </Reveal>
          <Reveal delay={0.75} className="flex gap-4">
            <Link to="/consultation" data-testid="hero-consultation-btn" className="btn-primary font-bold" style={{ background: "#C6A15B", color: "#1A1A1A" }}>
              Free Consultation <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/skin-analysis" data-testid="hero-analysis-btn" className="btn-primary font-bold" style={{ background: "#C6A15B", color: "#1A1A1A" }}>
              AI Skin Scan
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const text = ["CO2 Fractional Laser", "IPL Rejuvenation", "Cupping Therapy", "PRP Therapy", "IV Hydration", "HIFU Lifting", "Mesotherapy", "Weight Management", "AI Skin Analysis"];
  return (
    <div className="bg-[#1A1A1A] text-[#E4E0EB] py-5 overflow-hidden relative z-10">
      <div className="flex whitespace-nowrap animate-marquee">
        {[0, 1].map((r) => (
          <div key={r} className="flex items-center">
            {text.map((t, i) => (
              <span key={`${r}-${i}`} className="font-display text-2xl md:text-3xl italic mx-8 flex items-center gap-8">
                {t} <span className="text-[#B0872A] not-italic text-lg">✦</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Services() {
  return (
    <section id="services" className="max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-36 relative z-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
        <div>
          <p className="overline text-[#B0872A] mb-4">Our Expertise</p>
          <h2 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95] max-w-2xl">
            Three pillars of transformation
          </h2>
        </div>
        <p className="text-[#57515F] max-w-sm">
          Each treatment is tailored following a complimentary consultation and clinical skin mapping.
        </p>
      </div>

      <div className="space-y-6">
        {SERVICES.map((s, i) => (
          <Reveal key={s.key} delay={i * 0.05}>
            <div className="grid md:grid-cols-12 gap-8 items-center bg-[#E4E0EB] border border-[#DAD5E2] rounded-2xl p-6 md:p-10">
              <div className="md:col-span-5 hover-scale-img rounded-xl h-72 md:h-80">
                <img src={s.image} alt={s.name} className="w-full h-full object-cover rounded-xl" />
              </div>
              <div className="md:col-span-7 md:pl-6">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <span className="font-display text-6xl text-[#D8C38E]">0{i + 1}</span>
                  {s.startingAt && (
                    <Link to="/pricing" data-testid={`starting-at-${s.key}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wider uppercase text-[#1A1A1A] bg-gradient-to-r from-[#DCC17C] to-[#C6A15B] rounded-full px-3.5 py-1.5 shadow-sm hover:shadow-md transition-shadow">
                      Starting at {s.startingAt}
                    </Link>
                  )}
                </div>
                <h3 className="font-display text-4xl md:text-5xl tracking-tight mt-2 mb-1">{s.name}</h3>
                <p className="italic font-display text-2xl text-[#B0872A] mb-6">{s.tagline}</p>
                <div className="flex flex-wrap gap-2">
                  {s.treatments.map((t) => (
                    <span key={t} className="text-sm text-[#57515F] bg-white border border-[#DAD5E2] rounded-full px-4 py-1.5">
                      {t}
                    </span>
                  ))}
                </div>
                <div className="mt-6">
                  <Link to="/pricing" data-testid={`see-pricing-${s.key}`}
                    className="text-sm font-medium text-[#9A7420] hover:text-[#2B2340] transition-colors inline-flex items-center gap-1">
                    See full pricing <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
                  </Link>
                </div>
                {s.gallery && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
                    {s.gallery.map((g, gi) => (
                      <div key={gi} className="hover-scale-img rounded-lg h-28 md:h-32">
                        <img src={g} alt={`${s.name} ${gi + 1}`} className="w-full h-full object-cover rounded-lg" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Parameters() {
  return (
    <section id="analysis" className="on-dark bg-[#2B2340] text-[#EDEAF2] py-28 md:py-36 relative z-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-20">
          <p className="overline text-[#B0872A] mb-5">Clinical Intelligence</p>
          <h2 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95]">
            12 parameters. One complete portrait of your skin.
          </h2>
          <p className="text-white/60 mt-6 text-lg">
            Our diagnostic device evaluates both epidermal and dermal layers under RGB, PL
            and UV light — revealing what the eye cannot see.
          </p>
        </div>

        {/* Mobile: accordion (< md) */}
        <div className="md:hidden" data-testid="params-accordion">
          <Accordion type="single" collapsible className="w-full">
            {PARAMETERS.map((p, i) => (
              <AccordionItem key={p.name} value={p.name} className="border-white/15">
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="flex items-baseline gap-4">
                    <span className="font-display text-2xl text-[#B0872A] leading-none">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-body font-medium text-base text-white">{p.name}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-white/55 text-sm leading-relaxed pl-11">
                  {p.desc}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Desktop: grid (>= md) */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-2">
          {PARAMETERS.map((p, i) => (
            <Reveal key={p.name} delay={(i % 3) * 0.06}>
              <div className="border-t border-white/15 py-6 flex gap-5">
                <span className="font-display text-3xl text-[#B0872A] leading-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h4 className="font-body font-medium text-lg mb-1">{p.name}</h4>
                  <p className="text-white/55 text-sm leading-relaxed">{p.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <Link to="/skin-analysis" data-testid="params-analysis-btn" className="btn-primary bg-[#B0872A] text-[#1A1A1A]">
              <ScanFace className="w-5 h-5" strokeWidth={1.5} /> Try AI Skin Analysis
            </Link>
            <p className="text-white/50 text-sm">Upload a selfie — get an instant clinical breakdown.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function BeforeAfter() {
  const [pos, setPos] = useState(50);
  return (
    <section id="results" className="max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-36 relative z-10">
      <div className="mb-14">
        <p className="overline text-[#B0872A] mb-4">Proven Results</p>
        <h2 className="font-display text-5xl md:text-7xl tracking-tight">Before & after</h2>
      </div>
      <Reveal>
        <div
          data-testid="before-after-slider"
          className="relative w-full h-[420px] md:h-[600px] rounded-2xl overflow-hidden select-none cursor-ew-resize"
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setPos(Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100)));
          }}
        >
          <img src={AFTER_IMG} alt="After" className="absolute inset-0 w-full h-full object-cover" />
          <span className="absolute top-5 right-5 z-20 bg-white/80 backdrop-blur px-4 py-1 rounded-full text-xs uppercase tracking-widest">After</span>
          <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
            <img src={BEFORE_IMG} alt="Before" className="absolute inset-0 h-full object-cover" style={{ width: "100vw", maxWidth: "1216px" }} />
            <span className="absolute top-5 left-5 z-20 bg-[#1A1A1A]/70 text-white px-4 py-1 rounded-full text-xs uppercase tracking-widest">Before</span>
          </div>
          <div className="absolute top-0 bottom-0 z-10 w-0.5 bg-white" style={{ left: `${pos}%` }}>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-lg flex items-center justify-center">
              <ArrowUpRight className="w-4 h-4 rotate-45 text-[#1A1A1A]" />
            </div>
          </div>
        </div>
      </Reveal>
      <p className="text-[#8B8494] text-sm mt-4">Drag across the image · Representative results, individual outcomes vary.</p>
    </section>
  );
}

function ProductsPreview() {
  const [products, setProducts] = useState([]);
  const { addItem } = useCart();
  const navigate = useNavigate();
  useEffect(() => { getProducts().then((d) => setProducts(d.slice(0, 3))).catch(() => {}); }, []);
  return (
    <section id="shop" className="bg-[#E4E0EB] py-28 md:py-36 relative z-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between mb-14">
          <div>
            <p className="overline text-[#B0872A] mb-4">The Ritual</p>
            <h2 className="font-display text-5xl md:text-7xl tracking-tight">Shop skincare</h2>
          </div>
          <Link to="/shop" data-testid="view-all-products" className="hidden md:inline-flex items-center gap-2 text-[#1A1A1A] hover:gap-3 transition-all">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <div data-testid={`preview-product-${p.id}`} className="group">
                <div className="hover-scale-img bg-white rounded-xl h-96 mb-5 cursor-pointer" onClick={() => navigate(`/product/${p.id}`)}>
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover rounded-xl" />
                </div>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="overline text-[#B0872A] mb-1">{p.category}</p>
                    <h3 className="font-display text-2xl tracking-tight">{p.name}</h3>
                    <p className="text-[#57515F] text-sm">${p.price.toFixed(2)}</p>
                  </div>
                  <button data-testid={`preview-add-${p.id}`} onClick={() => addItem(p)} className="btn-secondary py-2 px-5 text-sm">Add</button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function InstagramFeed() {
  const [handle, setHandle] = useState("womenheavenmedspa");
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    let alive = true;
    import("../lib/api").then(({ getInstagramLinks }) => getInstagramLinks())
      .then((d) => { if (alive) { setHandle(d.handle || "womenheavenmedspa"); setPosts(d.posts || []); } })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const IG = `https://instagram.com/${handle}`;
  return (
    <section id="instagram" className="max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-36 relative z-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
        <div>
          <p className="overline text-[#B0872A] mb-4">Follow the glow</p>
          <a href={IG} target="_blank" rel="noreferrer" data-testid="ig-handle" className="font-display text-5xl md:text-7xl tracking-tight hover:text-[#B0872A] transition-colors">
            @{handle}
          </a>
        </div>
        <a href={IG} target="_blank" rel="noreferrer" data-testid="ig-follow" className="btn-primary font-bold" style={{ background: "#C6A15B", color: "#1A1A1A" }}>
          <Instagram className="w-5 h-5" strokeWidth={1.8} /> Follow on Instagram
        </a>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ig-grid">
        {posts.map((p, i) => (
          <Reveal key={i} delay={(i % 6) * 0.05}>
            <a href={p.url || IG} target="_blank" rel="noreferrer" className="group block relative aspect-square overflow-hidden rounded-xl hover-scale-img">
              <img src={p.image} alt={p.caption || `Women Heaven Instagram ${i + 1}`} className="w-full h-full object-cover" />
              <span className="absolute inset-0 bg-[#2B2340]/0 group-hover:bg-[#2B2340]/40 transition-colors flex items-center justify-center">
                <Instagram className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
              </span>
            </a>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <Hero />
      <Marquee />
      <Services />
      <Parameters />
      <BeforeAfter />
      <ProductsPreview />
      <InstagramFeed />
    </main>
  );
}
